import { useState, useMemo } from 'react';
import { Search, Users, MessageCircle, Eye, Phone, Mail, Clock, ArrowUp, ArrowDown, RefreshCw, Calendar, ChevronDown, ChevronUp, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { useAuth } from '@/hooks/useAuth';
import type { Lead, PaginatedResponse, ConversationPaginationResponse, Conversation } from '@/types/api.types';


// Helper functions
const getLeadDisplayName = (lead: Lead): string => {
  if (lead.full_name) return lead.full_name;
  if (lead.email) return lead.email;
  return `Cliente ${lead.phone.slice(-4)}`;
};

export const ClientLeadsView: React.FC = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    search: '',
    lead_status: '',
    email: '',
    phone: '',
    full_name: '',
    budget: '',
    date_from: '',
    date_to: '',
    last_message_date_start: '',
    last_message_date_end: '',
    sort_by: 'created_at' as 'created_at' | 'updated_at' | 'lead_status' | 'last_message_date' | 'full_name' | 'budget',
    sort_order: 'desc' as 'asc' | 'desc',
    page: 1,
    limit: 20
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showConversations, setShowConversations] = useState(false);

  // Get client ID from user context
  const clientId = user?.client_project_id;
  
  if (!clientId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-500 mb-2">Error: No se pudo identificar el cliente</p>
          <p className="text-sm text-gray-600">Por favor, vuelva a iniciar sesión</p>
        </div>
      </div>
    );
  }

  // Helper function to format date for input fields
  const formatDateForInput = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Fetch leads using the API with proper filtering
  const { data: leadsData, isLoading: leadsLoading, error: leadsError, refetch: refetchLeads, isRefetching } = useQuery<PaginatedResponse<Lead>>({
    queryKey: ['client-leads', clientId, filters],
    queryFn: async () => {
      const { clientApiService } = await import('@/services/api/client.service');
      
      // Build filter parameters
      const params: any = {
        page: filters.page,
        limit: filters.limit,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order
      };
      
      // Add optional filters only if they have values
      if (filters.search) params.search = filters.search;
      if (filters.lead_status && filters.lead_status !== 'all') params.lead_status = filters.lead_status;
      if (filters.email) params.email = filters.email;
      if (filters.phone) params.phone = filters.phone;
      if (filters.full_name) params.full_name = filters.full_name;
      if (filters.budget) params.budget = filters.budget;
      
      // Add date filters (date_from and date_to for creation dates)
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      
      // Add last message date filters
      if (filters.last_message_date_start) params.last_message_date_start = filters.last_message_date_start;
      if (filters.last_message_date_end) params.last_message_date_end = filters.last_message_date_end;
      
      const result = await clientApiService.getClientLeads(clientId, params);
      
      return {
        items: result.data,
        total: result.total,
        page: result.page,
        total_pages: result.total_pages,
        page_size: result.page_size
      } as PaginatedResponse<Lead>;
    }
  });

  // No more client-side filtering needed, all filtering is done by the API

  // Fetch conversations for selected lead
  const { data: conversationsData, isLoading: conversationsLoading } = useQuery<ConversationPaginationResponse | null>({
    queryKey: ['lead-conversations', selectedLead?.id],
    queryFn: async () => {
      if (!selectedLead) return null;
      const response = await apiClient.get<{ success: boolean; data: ConversationPaginationResponse; requestId: string }>(
        `/leads/${selectedLead.id}/conversations`
      );
      return response.data.data;
    },
    enabled: !!selectedLead && showConversations
  });

  // Calculate metrics based on data
  const calculatedMetrics = useMemo(() => {
    if (!leadsData?.items) {
      return {
        totalLeads: 0,
        newToday: 0,
        leadsWithConversations: 0
      };
    }

    const today = new Date().toDateString();
    const currentPageLeads = leadsData.items;
    
    // Calculate new leads today (from current page only)
    const newToday = currentPageLeads.filter(lead => {
      const leadDate = new Date(lead.created_at).toDateString();
      return leadDate === today;
    }).length;

    // Count leads that have conversations
    const leadsWithConversations = currentPageLeads.filter(lead => lead.conversation_count > 0).length;

    return {
      totalLeads: leadsData.total || 0,
      newToday,
      leadsWithConversations
    };
  }, [leadsData]);

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value, page: 1 }));
  };

  const handleFilterChange = (key: string, value: any) => {
    // When changing page, don't reset
    if (key === 'page') {
      setFilters(prev => ({ ...prev, [key]: value }));
    } else {
      // For other filters, reset to first page
      setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (leadStatus: string | null) => {
    // Handle null or undefined status
    if (!leadStatus) {
      return (
        <Badge variant="secondary" className="bg-gray-100 text-gray-600">
          Sin clasificar
        </Badge>
      );
    }
    
    // Temperature-based status mapping (used by both St Austin and Victoria)
    const temperatureStatusConfig: Record<string, { variant: any; className: string; label: string }> = {
      'Caliente': { variant: 'default', className: 'bg-red-100 text-red-800', label: 'Caliente 🔥' },
      'Tibio': { variant: 'default', className: 'bg-orange-100 text-orange-800', label: 'Tibio 🌡️' },
      'Frio': { variant: 'default', className: 'bg-blue-100 text-blue-800', label: 'Frío ❄️' },
      'Por calificar': { variant: 'outline', className: 'bg-gray-100 text-gray-700', label: 'Por calificar' }
    };
    
    // Check if status matches our known statuses
    const config = temperatureStatusConfig[leadStatus] || 
                   { variant: 'outline', className: 'bg-gray-100 text-gray-600', label: leadStatus };
    
    return (
      <Badge variant={config.variant} className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const handleViewConversations = (lead: Lead) => {
    setSelectedLead(lead);
    setShowConversations(true);
  };

  const handleRefresh = async () => {
    await refetchLeads();
  };

  if (leadsError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-500 mb-2">Error al cargar los leads</p>
          <p className="text-sm text-gray-600">{(leadsError as Error).message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold">Mis Leads</h1>
          <p className="text-gray-600">Gestión de clientes potenciales</p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={isRefetching || leadsLoading}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`} />
          {isRefetching ? 'Actualizando...' : 'Actualizar'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{calculatedMetrics.totalLeads}</div>
            <p className="text-xs text-muted-foreground">
              {filters.date_from || filters.date_to ? 'Período seleccionado' : 'Todos los tiempos'}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Nuevos Hoy</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{calculatedMetrics.newToday}</div>
            <p className="text-xs text-muted-foreground">
              Registrados hoy
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Con Conversaciones</CardTitle>
            <MessageCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {calculatedMetrics.leadsWithConversations}
            </div>
            <p className="text-xs text-muted-foreground">
              En esta página
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total en Período</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {calculatedMetrics.totalLeads}
            </div>
            <p className="text-xs text-muted-foreground">
              Leads registrados
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <CardTitle className="text-lg">Filtros</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="flex items-center gap-1"
            >
              {showAdvancedFilters ? (
                <><ChevronUp className="h-4 w-4" /> Ocultar filtros</>
              ) : (
                <><ChevronDown className="h-4 w-4" /> Más filtros</>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Basic filters - always visible */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar..."
                className="pl-10"
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
            
            <Select 
              value={filters.lead_status || 'all'} 
              onValueChange={(value) => handleFilterChange('lead_status', value === 'all' ? '' : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="new">Nuevo</SelectItem>
                <SelectItem value="Caliente">Caliente 🔥</SelectItem>
                <SelectItem value="Tibio">Tibio 🌡️</SelectItem>
                <SelectItem value="Frio">Frío ❄️</SelectItem>
                <SelectItem value="Por calificar">Por calificar</SelectItem>
              </SelectContent>
            </Select>
            
            <Input
              type="date"
              placeholder="Desde"
              value={formatDateForInput(filters.date_from)}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
            />
            
            <Input
              type="date"
              placeholder="Hasta"
              value={formatDateForInput(filters.date_to)}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
            />
          </div>
          
          {/* Advanced filters - collapsible */}
          {showAdvancedFilters && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                <Input
                  placeholder="Email"
                  value={filters.email}
                  onChange={(e) => handleFilterChange('email', e.target.value)}
                />
                
                <Input
                  placeholder="Teléfono"
                  value={filters.phone}
                  onChange={(e) => handleFilterChange('phone', e.target.value)}
                />
                
                <Input
                  placeholder="Nombre completo"
                  value={filters.full_name}
                  onChange={(e) => handleFilterChange('full_name', e.target.value)}
                />
                
                <Input
                  placeholder="Presupuesto"
                  value={filters.budget}
                  onChange={(e) => handleFilterChange('budget', e.target.value)}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                <div>
                  <label className="text-xs text-gray-600">Último mensaje desde</label>
                  <Input
                    type="date"
                    value={formatDateForInput(filters.last_message_date_start)}
                    onChange={(e) => handleFilterChange('last_message_date_start', e.target.value)}
                  />
                </div>
                
                <div>
                  <label className="text-xs text-gray-600">Último mensaje hasta</label>
                  <Input
                    type="date"
                    value={formatDateForInput(filters.last_message_date_end)}
                    onChange={(e) => handleFilterChange('last_message_date_end', e.target.value)}
                  />
                </div>
                
                <Select 
                  value={filters.sort_by}
                  onValueChange={(value: any) => handleFilterChange('sort_by', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Ordenar por" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at">Fecha creación</SelectItem>
                    <SelectItem value="updated_at">Actualización</SelectItem>
                    <SelectItem value="last_message_date">Último mensaje</SelectItem>
                    <SelectItem value="lead_status">Estado</SelectItem>
                    <SelectItem value="full_name">Nombre</SelectItem>
                    <SelectItem value="budget">Presupuesto</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleFilterChange('sort_order', filters.sort_order === 'asc' ? 'desc' : 'asc')}
                  className="flex items-center gap-2"
                >
                  {filters.sort_order === 'asc' ? (
                    <><ArrowUp className="h-4 w-4" /> Ascendente</>
                  ) : (
                    <><ArrowDown className="h-4 w-4" /> Descendente</>
                  )}
                </Button>
              </div>
            </>
          )}
          
          {/* Control buttons */}
          <div className="flex justify-between items-center mt-3 pt-3 border-t">
            <Select 
              value={filters.limit.toString()}
              onValueChange={(value) => handleFilterChange('limit', parseInt(value))}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 por pág.</SelectItem>
                <SelectItem value="20">20 por pág.</SelectItem>
                <SelectItem value="50">50 por pág.</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFilters({
                  search: '',
                  lead_status: '',
                  email: '',
                  phone: '',
                  full_name: '',
                  budget: '',
                  date_from: '',
                  date_to: '',
                  last_message_date_start: '',
                  last_message_date_end: '',
                  sort_by: 'created_at',
                  sort_order: 'desc',
                  page: 1,
                  limit: 20
                });
                setShowAdvancedFilters(false);
              }}
            >
              Limpiar filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Leads</CardTitle>
          <CardDescription>
            {leadsData ? `Total: ${leadsData.total} leads` : 'Cargando...'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {leadsLoading ? (
            <div className="flex items-center justify-center h-32">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Presupuesto</TableHead>
                    <TableHead>Conversaciones</TableHead>
                    <TableHead>Último Mensaje</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(leadsData?.items || []).map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell className="font-mono text-sm">
                        #{lead.original_lead_id}
                      </TableCell>
                      <TableCell className="font-medium">
                        {getLeadDisplayName(lead)}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {lead.email && (
                            <div className="flex items-center gap-1 text-sm">
                              <Mail className="h-3 w-3" />
                              {lead.email}
                            </div>
                          )}
                          <div className="flex items-center gap-1 text-sm">
                            <Phone className="h-3 w-3" />
                            {lead.phone}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(lead.lead_status)}</TableCell>
                      <TableCell>
                        {lead.budget || <span className="text-gray-400">No especificado</span>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {lead.conversation_count || 0}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {lead.last_message_date ? formatDate(lead.last_message_date) : <span className="text-gray-400">-</span>}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewConversations(lead)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {leadsData && (
                <div className="flex justify-between items-center mt-4">
                  <span className="text-sm text-gray-600">
                    Página {leadsData.page} de {leadsData.total_pages || 1} ({leadsData.total || 0} total)
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleFilterChange('page', filters.page - 1)}
                      disabled={filters.page <= 1}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleFilterChange('page', filters.page + 1)}
                      disabled={!leadsData.total_pages || filters.page >= leadsData.total_pages}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Lead Detail & Conversations Dialog */}
      <Dialog open={showConversations} onOpenChange={setShowConversations}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {selectedLead?.full_name || 'Detalle del Lead'}
            </DialogTitle>
            <DialogDescription>
              Información completa y conversaciones del lead
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="info" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="info">Información</TabsTrigger>
              <TabsTrigger value="conversations">Conversaciones</TabsTrigger>
            </TabsList>
            
            <TabsContent value="info" className="space-y-4">
              {selectedLead && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">ID Lead</p>
                    <p className="font-mono">#{selectedLead.original_lead_id}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Nombre</p>
                    <p>{selectedLead.full_name || 'No disponible'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Email</p>
                    <p>{selectedLead.email || 'No disponible'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Teléfono</p>
                    <p>{selectedLead.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Estado</p>
                    <p>{getStatusBadge(selectedLead.lead_status)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Presupuesto</p>
                    <p>{selectedLead.budget || 'No especificado'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total conversaciones</p>
                    <p>{selectedLead.conversation_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Último mensaje</p>
                    <p>{selectedLead.last_message_date ? formatDate(selectedLead.last_message_date) : 'Sin mensajes'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Última interacción</p>
                    <p>{selectedLead.last_interaction ? formatDate(selectedLead.last_interaction) : 'Sin interacción'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Creado</p>
                    <p>{formatDate(selectedLead.created_at)}</p>
                  </div>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="conversations" className="h-[400px] overflow-y-auto">
              {conversationsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <LoadingSpinner />
                </div>
              ) : conversationsData && conversationsData.conversations && conversationsData.conversations.length > 0 ? (
                <div className="space-y-4">
                  {[...conversationsData.conversations]
                    .sort((a: Conversation, b: Conversation) => 
                      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
                    )
                    .map((conv: Conversation) => (
                    <div 
                      key={conv.id} 
                      className={`p-3 rounded-lg ${
                        conv.sender_type === 'user' 
                          ? 'bg-blue-50 ml-8' 
                          : 'bg-gray-50 mr-8'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-sm font-medium">
                          {conv.sender_type === 'user' ? 'Cliente' : 'Bot'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(conv.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm">{conv.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-32 text-gray-500">
                  No hay conversaciones disponibles
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};